import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const pillars = [
  { num: '01', icon: 'FiMic', accent: '#289199', title: 'Voice-Matched AI Replies', short: 'Sounds exactly like you. Every time.', body: "We don't use generic templates. Our AI crafts authentic, on-brand responses that close the feedback loop and teach the algorithm exactly who you are." },
  { num: '02', icon: 'FiSearch', accent: '#eb5e55', title: 'The Stealth SEO Booster', short: 'Invisible leverage. Visible results.', body: 'While writing tailored responses, the AI strategically embeds local SEO keywords, specific services, and geographic locations directly into the reply.' },
  { num: '03', icon: 'FiBarChart2', accent: '#f59e0b', title: 'Amplifying the Right Signals', short: 'Feed the algorithm. Win the ranking.', body: 'By responding to your past wins, we feed the algorithm massive positive reinforcement — directly strengthening your relevance signals.' },
];

const before = ['Unanswered reviews rotting in a graveyard', 'Algorithm sees a silent, inactive profile', 'Competitors outranking you on engagement', 'Historical SEO value = zero'];
const after = ['Every past review retroactively optimized', 'Algorithm sees a hyper-active local authority', 'Keywords embedded in every reply', 'Historical SEO value = compounding asset'];

function PillarCard({ pillar, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.1 }} className="group relative flex flex-col p-4 sm:p-5 rounded-2xl bg-[#202020] border border-white/5 overflow-hidden">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl" style={{ boxShadow: `inset 0 0 0 1px ${pillar.accent}40, 0 0 30px ${pillar.accent}10` }} />
      <div className="relative z-10 flex flex-col h-full gap-2">
        <div className="flex items-center justify-between">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${pillar.accent}20`, border: `1px solid ${pillar.accent}40` }}>
            <SafeIcon name={pillar.icon} className="h-4 w-4" style={{ color: pillar.accent }} />
          </div>
          <span className="text-3xl font-michroma text-white/5">{pillar.num}</span>
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: pillar.accent }}>{pillar.short}</span>
        <h3 className="text-xs sm:text-sm font-michroma text-white leading-snug">{pillar.title}</h3>
        <AnimatePresence>
          {open && (
            <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="text-xs text-white/50 leading-relaxed overflow-hidden">{pillar.body}</motion.p>
          )}
        </AnimatePresence>
        <button onClick={() => setOpen(!open)} className="self-start flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest mt-auto" style={{ color: pillar.accent }}>
          {open ? 'Collapse' : 'Learn More'}
          <SafeIcon name={open ? 'FiChevronUp' : 'FiChevronDown'} className="h-3 w-3" />
        </button>
      </div>
    </motion.div>
  );
}

function CompareRow({ text, positive }) {
  return (
    <div className="flex items-start gap-2">
      <div className={`flex-shrink-0 h-4 w-4 rounded-full flex items-center justify-center mt-0.5 ${positive ? 'bg-brand-teal/20' : 'bg-[#eb5e55]/15'}`}>
        <SafeIcon name={positive ? 'FiCheck' : 'FiX'} className="h-2.5 w-2.5" style={{ color: positive ? '#289199' : '#eb5e55' }} />
      </div>
      <span className={`text-xs leading-relaxed ${positive ? 'text-white/70' : 'text-white/35 line-through'}`}>{text}</span>
    </div>
  );
}

export default function AuthorityBacklog() {
  return (
    <section className="relative w-full min-h-full bg-[#202020] flex flex-col justify-center py-24 px-4 sm:px-6 md:px-12 overflow-hidden font-lexend">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-teal/40 to-transparent" />
      
      <div className="relative z-10 max-w-6xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-5 sm:mb-8">
          <span className="inline-block text-brand-teal font-bold text-xs uppercase tracking-[0.2em] mb-3">Historical SEO Reply</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-michroma text-white tracking-tight leading-tight">
            The <span className="text-brand-teal drop-shadow-[0_0_20px_rgba(40,145,153,0.35)]">Authority Backlog</span> Engine
          </h2>
          <p className="mt-3 text-white/55 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
            Your past reviews are sitting in a graveyard of wasted potential. <span className="text-white/80 font-semibold">By leaving them unanswered, you're starving your own SEO.</span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-5 sm:mb-6">
          {pillars.map((p, i) => <PillarCard key={i} pillar={p} index={i} />)}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div className="rounded-2xl border border-white/5 bg-[#181818] p-4 sm:p-5 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-[#eb5e55]/15 border border-[#eb5e55]/25 flex items-center justify-center">
                <SafeIcon name="FiAlertCircle" className="h-3.5 w-3.5 text-[#eb5e55]" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#eb5e55]">Before</span>
            </div>
            <div className="flex flex-col gap-2">{before.map((t, i) => <CompareRow key={i} text={t} positive={false} />)}</div>
          </div>
          <div className="rounded-2xl border border-brand-teal/20 bg-[#181818] p-4 sm:p-5 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-brand-teal/15 border border-brand-teal/25 flex items-center justify-center">
                <SafeIcon name="FiZap" className="h-3.5 w-3.5 text-brand-teal" />
              </div>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-teal">After</span>
            </div>
            <div className="flex flex-col gap-2">{after.map((t, i) => <CompareRow key={i} text={t} positive={true} />)}</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="rounded-2xl border border-white/5 bg-[#181818] p-4 sm:p-5 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-teal mb-2">The Transformation</p>
          <p className="text-white/55 text-xs leading-relaxed max-w-2xl mx-auto">
            You instantly shift from a static, quiet profile to a <span className="text-white/80 font-semibold">hyper-active, hyper-relevant local authority.</span> Every great review you've ever received is retroactively optimized to help future customers find you faster.
          </p>
        </motion.div>
      </div>
    </section>
  );
}